import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { registerUser } from '../redux/slices/userSlice';
import { useNotifications } from '../contexts/NotificationContext';

const Register: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector(state => state.user);
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'viewer' as const,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const roles = [
    { value: 'viewer', label: 'Visualizador' },
    { value: 'doctor', label: 'Doctor' },
    { value: 'nurse', label: 'Enfermero/a' },
    { value: 'technician', label: 'Técnico' },
  ] as const;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
    }

    if (!formData.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    if (!formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.length < 8) {
      newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        'La contraseña debe contener al menos una mayúscula, una minúscula y un número';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirma tu contraseña';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(
        registerUser({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email,
          password: formData.password,
          role: formData.role,
        })
      ).unwrap();

      addNotification({
        type: 'success',
        title: 'Registro exitoso',
        message: 'Tu cuenta ha sido creada correctamente',
      });

      navigate('/dashboard');
    } catch (error: unknown) {
      addNotification({
        type: 'error',
        title: 'Error al registrarse',
        message: (error as string) || 'Hubo un problema al crear tu cuenta',
      });
    }
  };

  return (
    <Layout showSidebar={false}>
      <div className='min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <div className='text-center'>
            <div className='mx-auto h-12 w-12 bg-insurance-600 rounded-lg flex items-center justify-center'>
              <span className='text-white font-bold text-xl'>F</span>
            </div>
            <h2 className='mt-6 text-3xl font-bold text-gray-900'>Crear Cuenta</h2>
            <p className='mt-2 text-sm text-gray-600'>Únete a Ficohsa Seguros</p>
          </div>

          <form className='mt-8 space-y-6' onSubmit={handleSubmit}>
            <div className='space-y-4'>
              <div className='grid grid-cols-2 gap-4'>
                <Input
                  type='text'
                  name='firstName'
                  label='Nombre'
                  placeholder='Tu nombre'
                  value={formData.firstName}
                  onChange={handleChange}
                  error={errors.firstName}
                  required
                  disabled={isLoading}
                />

                <Input
                  type='text'
                  name='lastName'
                  label='Apellido'
                  placeholder='Tu apellido'
                  value={formData.lastName}
                  onChange={handleChange}
                  error={errors.lastName}
                  required
                  disabled={isLoading}
                />
              </div>

              <Input
                type='email'
                name='email'
                label='Email'
                placeholder='tu@email.com'
                value={formData.email}
                onChange={handleChange}
                error={errors.email}
                required
                disabled={isLoading}
              />

              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>Rol</label>
                <select
                  name='role'
                  value={formData.role}
                  onChange={handleChange}
                  className='input-field'
                  disabled={isLoading}
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              <Input
                type={showPassword ? 'text' : 'password'}
                name='password'
                label='Contraseña'
                placeholder='Mínimo 8 caracteres'
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                required
                disabled={isLoading}
                rightIcon={
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='text-gray-400 hover:text-gray-600'
                  >
                    {showPassword ? (
                      <EyeSlashIcon className='h-5 w-5' />
                    ) : (
                      <EyeIcon className='h-5 w-5' />
                    )}
                  </button>
                }
              />

              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                name='confirmPassword'
                label='Confirmar Contraseña'
                placeholder='Repite tu contraseña'
                value={formData.confirmPassword}
                onChange={handleChange}
                error={errors.confirmPassword}
                required
                disabled={isLoading}
                rightIcon={
                  <button
                    type='button'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className='text-gray-400 hover:text-gray-600'
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className='h-5 w-5' />
                    ) : (
                      <EyeIcon className='h-5 w-5' />
                    )}
                  </button>
                }
              />
            </div>

            <div className='flex items-center'>
              <input
                id='terms'
                name='terms'
                type='checkbox'
                required
                className='h-4 w-4 text-insurance-600 focus:ring-insurance-500 border-gray-300 rounded'
              />
              <label htmlFor='terms' className='ml-2 block text-sm text-gray-900'>
                Acepto los{' '}
                <Link to='/terms' className='text-insurance-600 hover:text-insurance-500'>
                  términos y condiciones
                </Link>{' '}
                y la{' '}
                <Link to='/privacy' className='text-insurance-600 hover:text-insurance-500'>
                  política de privacidad
                </Link>
              </label>
            </div>

            <div>
              <Button
                type='submit'
                variant='primary'
                size='lg'
                className='w-full'
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
              </Button>
            </div>

            <div className='text-center'>
              <p className='text-sm text-gray-600'>
                ¿Ya tienes una cuenta?{' '}
                <Link
                  to='/login'
                  className='font-medium text-insurance-600 hover:text-insurance-500'
                >
                  Inicia sesión aquí
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
